/**
 * 이상한 방 - 단계 시작 전 안내 (순수 DOM)
 *
 * 근거 문서: 상태모델 5.4절(동사의 단계적 도입) · 9.5절(싱글 = 튜토리얼)
 *           컨셉시트 1절("처음 보는 사람이 30초 안에 규칙을 이해")
 *
 * ## 규칙 하나: 읽을 것을 만들지 않는다
 *
 * 튜토리얼 텍스트를 읽게 만들면 사람들은 읽지 않고 건너뛴다. 건너뛴 뒤에는
 * 안내가 있었다는 사실만 남고 내용은 남지 않으므로, **긴 안내는 없는 안내보다 나쁘다.**
 * 개발 시간을 쓰고 아무것도 전달하지 못한 데다 시작 버튼까지 한 번 더 멀어졌다.
 *
 * 그래서 이 화면은 **한 문장 + 규칙 칩 + 시작 버튼**이 전부다. 산문은 정확히 한 줄이고
 * 나머지는 숫자와 칩이다. 숫자는 읽는 것이 아니라 보는 것이라 건너뛰기 전에 이미 눈에 들어온다.
 *
 * ## 이상한 곳의 종류 칩이 이 화면의 진짜 내용이다
 *
 * 10단계의 목적은 "어려운 판을 이기는 것"이 아니라 **멀티 규칙을 다 익힌 상태로 내보내는 것**이다
 * (상태모델 5.4절이 종류를 한꺼번에 주지 않는 이유와 같다).
 * 그러므로 매 단계에서 전달해야 할 단 하나의 사실은 "이번에 뭐가 새로 생겼는가"이며,
 * 그것이 표식이 붙은 칩 하나다. 칩 순서를 단계마다 다시 정렬하지 않고 도입 순서로 고정하는
 * 이유도 이것이다. 새 칩은 항상 줄 끝에 나타나야 눈이 그리로 간다.
 *
 * ## 카드가 아니라 전면 자막판인 이유
 *
 * 모달 카드는 유리 패널의 문법이고(이 게임의 시각 규칙이 버린 것 - `gameHud.css`), 실무적으로는
 * **카드 높이가 화면에 눌릴 때 제일 먼저 잘리는 것이 아래쪽 버튼**이다.
 * 그건 이 화면을 넘길 방법이 사라졌다는 뜻이다.
 * 그래서 전면 먹색 자막판을 깔고, 시작 버튼 줄만 스크롤 밖에 고정한다.
 * 잘려도 되는 것은 안내이지 출구가 아니다.
 *
 * ## 스타일은 `levelSelect.css`에 함께 있다
 *
 * 두 화면은 수명이 이어져 있다(선택 → 안내 → 게임 → 다시 선택).
 * 한쪽만 고쳐 두 화면이 다른 게임처럼 보이는 실패가 조용히 생기는 것을 막는다.
 */

import { MS_PER_SECOND } from '../core/time'
import type { Verb } from '../core/types'
import type { KitId } from '../game/economy/kits'
import { t } from '../i18n'

import { createElement } from './dom'
import { formatClock } from './gameHud'
import { createKitPick, type KitPickView } from './kitPick'
import { levelHintOf, levelNameOf, toVerbArray, type LevelSelectLevel } from './levelSelect'
import { attachScrollFade } from './scrollFade'
import { isSettingsOpen } from './settings'
import './levelSelect.css'

/* --- 표시 상수 ----------------------------------------------------------------- */

/**
 * 이상한 곳의 종류 표시 순서. 상태모델 5.4절의 도입 순서 그대로다.
 *
 * 단계마다 정렬을 다시 하지 않는 이유: 순서가 흔들리면 이미 배운 칩들이 자리를 바꾸고,
 * 그러면 "새로 생긴 것"을 찾는 데 시선이 한 번 더 든다. 새 칩은 항상 줄 끝에 있어야 한다.
 */
const VERB_ORDER: readonly Verb[] = ['remove', 'add', 'move', 'rotate', 'recolor']

/* --- 타입 --------------------------------------------------------------------- */

export type LevelIntroView = Readonly<{
  /** 시작할 단계. 선택 화면과 같은 구조적 계약을 쓴다. */
  level: LevelSelectLevel
  /** 화면상의 순서(1부터). "3단계"의 3이며, `level.id`와 다를 수 있다. */
  order: number
  /** 이번 단계의 시작 탄환. 단계별 값이 없으면 호출부가 `tuning.startAmmo`를 넘긴다. */
  ammo: number
  /**
   * 직전 단계. 새로 열린 종류를 여기서 역산한다.
   * 1단계이거나 알 수 없으면 `null`. 그때는 전부 새것이다.
   */
  previousLevel?: LevelSelectLevel | null
  /**
   * 이번 판의 실제 수색 시간(초). **준비물이 얹힌 뒤의 값**이라 단계 표와 다를 수 있다(로드맵 M9).
   * 없으면 단계 표의 값을 쓴다 - 탄환(`ammo`)이 이미 같은 규약이고, 두 숫자가 다른 출처를 보면
   * "느린 시계를 챙겼는데 안내는 그대로"라는, 화면만 거짓말하는 상태가 된다.
   */
  huntSeconds?: number | undefined
  /**
   * 준비물 3택1(로드맵 M9). 없으면 슬롯이 접힌다 - 매일의 방·자유 모드처럼 준비물이 없는 자리가 있다.
   * `handlers.onPickKit`과 함께 있어야 슬롯이 선다(만들지 않는 쪽이 감추는 쪽보다 안전하다).
   */
  kit?: KitPickView | undefined
}>

export type LevelIntroHandlers = Readonly<{
  /** 시작. 이 화면의 존재 이유이자 유일하게 큰 버튼이다. */
  onStart: () => void
  /** 단계 선택으로 되돌아간다. */
  onBack: () => void
  /** 준비물을 골랐다(같은 것을 다시 누르면 `null` - 빈손). 없으면 슬롯 자체를 만들지 않는다. */
  onPickKit?: ((id: KitId | null) => void) | undefined
}>

export type LevelIntroHandle = Readonly<{
  show: (view: LevelIntroView) => void
  hide: () => void
  dispose: () => void
}>

export type LevelIntroOptions = Readonly<{
  mount: HTMLElement
  handlers: LevelIntroHandlers
}>

/* --- 종류 계산 ----------------------------------------------------------------- */

/** 이번 단계에 나오는 종류를 도입 순서대로. 목록에 없는 값은 그릴 문구가 없으므로 버린다. */
function orderedVerbs(level: LevelSelectLevel): readonly Verb[] {
  const allowed = new Set(toVerbArray(level.allowedVerbs))
  return VERB_ORDER.filter((verb) => allowed.has(verb))
}

/**
 * 직전 단계에 없던 종류 = 이번에 배울 것.
 *
 * 직전 단계를 모르면 **전부 새것으로 본다**. 반대로(전부 구식으로) 처리하면
 * 1단계에서 배울 것이 하나도 없다고 말하게 되는데, 그게 정확히 튜토리얼의 첫 화면이다.
 */
function newVerbsOf(view: LevelIntroView): ReadonlySet<Verb> {
  const current = orderedVerbs(view.level)
  const previous = view.previousLevel
  if (previous === undefined || previous === null) return new Set(current)

  const known = new Set(toVerbArray(previous.allowedVerbs))
  return new Set(current.filter((verb) => !known.has(verb)))
}

/* --- DOM 헬퍼 ------------------------------------------------------------------ */

/**
 * 규칙 칸 하나. 값 노드를 돌려주어 `show`가 갱신 대상만 들고 있게 한다.
 * 라벨이 위, 값이 아래인 것은 결과 화면의 스탯 줄(`.rs-stat`)과 같은 순서다.
 */
function createRule(parent: HTMLElement, label: string): HTMLElement {
  const tile = createElement('div', 'li-rule')
  tile.append(createElement('span', 'li-rule__label', label))
  const value = createElement('strong', 'li-rule__value', '')
  tile.append(value)
  parent.append(tile)
  return value
}

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 단계 안내를 만들어 `mount`에 붙인다. 처음에는 숨겨져 있다.
 *
 * 상태를 갖지 않는다. 무엇을 시작할지도, 다음에 무엇을 띄울지도 전부 바깥의 몫이다.
 */
export function createLevelIntro(options: LevelIntroOptions): LevelIntroHandle {
  const root = createElement('div', 'li-root')
  root.hidden = true
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-modal', 'true')
  root.setAttribute('aria-labelledby', 'li-name')

  const sheet = createElement('div', 'li-sheet')

  const step = createElement('p', 'li-step')
  const name = createElement('h1', 'li-name')
  name.id = 'li-name'

  /** 이 화면의 유일한 산문. 한 문장을 넘기지 않는 것은 사전을 쓰는 쪽의 책임이다. */
  const hint = createElement('p', 'li-hint')

  const rules = createElement('div', 'li-rules')
  const oddityValue = createRule(rules, t('intro.ruleOddities'))
  const timeValue = createRule(rules, t('intro.ruleTime'))
  const ammoValue = createRule(rules, t('intro.ruleAmmo'))

  const verbs = createElement('div', 'li-verbs')
  verbs.setAttribute('aria-label', t('intro.verbsLabel'))

  /*
   * 준비물 슬롯(로드맵 M9). 동사 칩 **아래**다 - 이 화면이 먼저 전달해야 하는 것은 "이번에 뭐가 새로 생겼는가"이고
   * (머리말), 준비물은 그것을 읽은 뒤의 선택이다. 로직은 `ui/kitPick.ts`에 있고 여기서는 붙이고 그릴 뿐이다.
   */
  const kitPick = options.handlers.onPickKit === undefined ? null : createKitPick(options.handlers.onPickKit)

  const actions = createElement('div', 'li-actions')
  const startButton = createElement('button', 'li-start', t('intro.start'))
  startButton.type = 'button'
  const backButton = createElement('button', 'li-back', t('intro.back'))
  backButton.type = 'button'
  actions.append(startButton, backButton)

  /*
   * 버튼 줄은 스크롤 밖이다(`resultScreen`의 액션 줄과 같은 구조).
   * 가로로 눕힌 폰에서 잘려 나가는 것이 시작 버튼이면 **이 화면을 넘길 방법이 사라진다.**
   */
  const scroll = createElement('div', 'li-scroll')
  scroll.append(step, name, hint, rules, verbs)
  if (kitPick !== null) scroll.append(kitPick.root)
  sheet.append(scroll, actions)
  root.append(sheet)
  options.mount.append(root)

  /*
   * 스크롤 끝 신호(`scrollFade.ts`). 가로로 눕힌 폰에서 안내가 잘리는데, 잘린 것이 있다는
   * 사실 자체가 보이지 않으면 사람은 규칙 칩을 못 본 채로 판에 들어간다.
   * 두께는 남은 거리에 비례하므로 바닥에 닿는 순간 0이 되고, 마지막 줄이 흐려지지 않는다.
   */
  const stopScrollFade = attachScrollFade(scroll)

  function renderVerbs(view: LevelIntroView): void {
    verbs.replaceChildren()

    const isNew = newVerbsOf(view)
    for (const verb of orderedVerbs(view.level)) {
      const fresh = isNew.has(verb)
      const chip = createElement('span', fresh ? 'li-verb is-new' : 'li-verb')
      chip.append(createElement('span', 'li-verb__text', t(`verb.${verb}`)))
      // 색만으로 구분하지 않는다. 이 표식이 색각 대응이자 본문이다.
      if (fresh) chip.append(createElement('span', 'li-verb__new', t('intro.newBadge')))
      verbs.append(chip)
    }
  }

  /**
   * Enter = 시작, Esc = 뒤로. 데스크톱에서 혼자 수십 판을 돌리는 것이 이번 단계의 작업 방식이라
   * 안내 화면을 마우스로 넘기는 비용이 그대로 시행 횟수를 깎는다.
   */
  function handleKeyDown(event: KeyboardEvent): void {
    if (root.hidden) return
    // 설정 패널이 위에 떠 있으면 키는 그쪽의 것이다. Esc 한 번이 패널을 닫으면서 동시에
    // 단계 선택으로 나가면, 그건 한 번 누른 키가 두 가지 일을 한 것이다.
    if (isSettingsOpen()) return
    if (event.altKey || event.ctrlKey || event.metaKey) return

    if (event.key === 'Enter') {
      // 버튼에 포커스가 있으면 브라우저가 이미 클릭을 만든다. 두 번 시작되지 않게 비켜준다.
      if (document.activeElement instanceof HTMLButtonElement) return
      event.preventDefault()
      options.handlers.onStart()
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      options.handlers.onBack()
    }
  }

  startButton.addEventListener('click', options.handlers.onStart)
  backButton.addEventListener('click', options.handlers.onBack)
  window.addEventListener('keydown', handleKeyDown)

  return {
    show(view: LevelIntroView): void {
      step.textContent = t('intro.step', { order: view.order })
      name.textContent = levelNameOf(view.level)
      hint.textContent = levelHintOf(view.level)

      oddityValue.textContent = t('intro.oddityValue', { count: view.level.changeCount })
      // 준비물이 얹힌 값이 오면 그것이 이번 판의 진실이다(위 `huntSeconds` 주석).
      timeValue.textContent = formatClock((view.huntSeconds ?? view.level.huntSeconds) * MS_PER_SECOND)
      ammoValue.textContent = t('intro.ammoValue', { count: view.ammo })

      renderVerbs(view)
      if (kitPick !== null) {
        kitPick.root.hidden = view.kit === undefined
        if (view.kit !== undefined) kitPick.render(view.kit)
      }

      root.hidden = false
      // 포커스를 시작 버튼에 올려 둔다. 안내를 읽지 않기로 한 사람은 Enter 한 번이면 끝나야 한다.
      startButton.focus()
    },

    hide(): void {
      if (root.contains(document.activeElement)) {
        startButton.blur()
      }
      root.hidden = true
    },

    dispose(): void {
      stopScrollFade()
      kitPick?.dispose()
      startButton.removeEventListener('click', options.handlers.onStart)
      backButton.removeEventListener('click', options.handlers.onBack)
      window.removeEventListener('keydown', handleKeyDown)
      root.remove()
    },
  }
}
